import React from 'react'
import {
  arrayOf,
  array,
  number,
  shape,
  string,
  oneOf,
  object,
  bool
} from 'prop-types'
import { ButtonGroup, Highlight } from '@/component/Primitive/Events'
import styles from './Block.module.scss'
import ResponsiveImage from '@/component/Primitive/ResponsiveImage'
import cx from 'classnames'
import Stack from '@/component/Primitive/Stack'
import Hide from '@/component/Primitive/Hide'
import Type from '@/component/Primitive/Type'

const Block = ({
  tag,
  title,
  subtitle,
  description,
  image,
  theme,
  ctaLabel,
  buttonGroup,
  inverse,
  logo,
  fullUrlPath
}) => {
  const { mobile: mobileImage, desktop: desktopImage } = image || {}
  const { mobile: mobileLogo, desktop: deskotpLogo } = logo || {}

  const getUrl = () => {
    if (!fullUrlPath) return null
    if (fullUrlPath.includes('http:') || fullUrlPath.includes('https:'))
      return fullUrlPath
    return `/${fullUrlPath}`
  }

  const extendedButtonGroup = []
  if (getUrl()) {
    extendedButtonGroup.push({
      type: 'link',
      variant: 'secondary',
      label: ctaLabel,
      link: getUrl()
    })
  }
  if (buttonGroup?.length) {
    extendedButtonGroup.push(...buttonGroup)
  }

  return (
    <div className={cx(styles.Block, theme === 'dark' && styles.dark)}>
      <div className={cx(styles.BlockInner, inverse && styles.inverse)}>
        <div className={styles.ImagesWrapper}>
          {desktopImage && (
            <Image device="desktop" image={desktopImage} logo={deskotpLogo} />
          )}
          {mobileImage && (
            <Image device="mobile" image={mobileImage} logo={mobileLogo} />
          )}
        </div>
        <div className={styles.ContentWrapper}>
          <div className={styles.Content}>
            <Stack gap="medium">
              {tag && (
                <Type
                  as="h3"
                  size={['body2', 'body3']}
                  weight="medium"
                  className={styles.Tag}
                >
                  {tag}
                </Type>
              )}
              <Stack gap="tiny">
                {title && (
                  <Type
                    as="h1"
                    size={['heading5', 'heading2']}
                    weight="medium"
                    className={styles.Title}
                  >
                    <Highlight>{title}</Highlight>
                  </Type>
                )}
                {subtitle && (
                  <Type
                    as="h2"
                    size={['body2', 'body3']}
                    weight="medium"
                    className={styles.Subtitle}
                  >
                    {subtitle}
                  </Type>
                )}
              </Stack>
              {description && (
                <Type as="div" size={['body2', 'body2']}>
                  <div dangerouslySetInnerHTML={{ __html: description }} />
                </Type>
              )}
              <div className={styles.Buttons}>
                {!!extendedButtonGroup?.length && (
                  <ButtonGroup group={extendedButtonGroup} />
                )}
              </div>
            </Stack>
          </div>
        </div>
      </div>
    </div>
  )
}

Block.propTypes = {
  tag: string,
  title: string,
  subtitle: string,
  description: string,
  theme: string,
  ctaLabel: string,
  buttonGroup: array,
  fullUrlPath: string,
  inverse: bool,
  image: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  }),
  logo: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  })
}

const Image = ({ device, image, logo }) => {
  const hideProps =
    device === 'mobile' ? { at: 'desktop' } : { below: 'desktop' }
  const className =
    device === 'mobile' ? styles.mobileImage : styles.desktopImage
  return (
    <Hide {...hideProps} className={styles.ImageWrapper}>
      <ResponsiveImage className={cx(styles.Image, className)} {...image} />
      {logo && (
        <ResponsiveImage className={cx(styles.Logo, className)} {...logo} />
      )}
    </Hide>
  )
}

Image.propTypes = {
  device: oneOf(['mobile', 'desktop']),
  image: object,
  logo: object
}

export default Block
